import { forwardRef, type ComponentPropsWithoutRef } from "react";
import { Button as MantineButton, type ButtonProps as MantineButtonProps } from "@mantine/core";

type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";
type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps
  extends Omit<MantineButtonProps & ComponentPropsWithoutRef<"button">, "variant" | "size"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
}

const variantMap: Record<
  ButtonVariant,
  { variant: MantineButtonProps["variant"]; color?: string }
> = {
  primary: { variant: "filled", color: "primary" },
  secondary: { variant: "filled", color: "#0f3460" },
  danger: { variant: "filled", color: "red" },
  ghost: { variant: "subtle", color: "gray" },
};

const sizeMap: Record<ButtonSize, MantineButtonProps["size"]> = {
  sm: "xs",
  md: "sm",
  lg: "md",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = "primary", size = "md", isLoading = false, disabled, children, ...props }, ref) => {
    const { variant: mantineVariant, color } = variantMap[variant];

    return (
      <MantineButton
        ref={ref}
        variant={mantineVariant}
        color={color}
        size={sizeMap[size]}
        loading={isLoading}
        disabled={disabled}
        {...props}
      >
        {children}
      </MantineButton>
    );
  }
);

Button.displayName = "Button";
